import { TooltipDirective } from '../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../componentes/botones';
import { Component, forwardRef, Input } from '@angular/core';

import {
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { IConfigHorario } from 'modelos/src';
import { TimePickerComponent } from '../../../componentes/time-picker/time-picker.component';
import { ClienteSelectorComponent } from '../../../componentes/selectores/cliente-selector/cliente-selector.component';
import { NgSelectModule } from '@ng-select/ng-select';

interface IConfigHorarioForm {
  dias: FormControl<number[] | null>;
  horaInicio: FormControl<string | null>;
  horaFin: FormControl<string | null>;
}

const DIAS = [
  { valor: 0, label: 'Domingo' },
  { valor: 1, label: 'Lunes' },
  { valor: 2, label: 'Martes' },
  { valor: 3, label: 'Miércoles' },
  { valor: 4, label: 'Jueves' },
  { valor: 5, label: 'Viernes' },
  { valor: 6, label: 'Sábado' },
];

@Component({
  selector: 'app-config-horarios-atencion',
  standalone: true,
  imports: [TooltipDirective, 
    NgSelectModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    BOTONES,
    MatCardModule,
    MatExpansionModule,
    
    TimePickerComponent,
    ClienteSelectorComponent,
  ],
  templateUrl: './config-horarios-atencion.component.html',
  styleUrl: './config-horarios-atencion.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ConfigHorariosAtencionComponent),
      multi: true,
    },
  ],
})
export class ConfigHorariosAtencionComponent implements ControlValueAccessor {
  @Input() idClienteFinal: string | null = null;
  @Input() titulo = 'Clientes que Atienden Eventos';

  clientesSelectorControl = new FormControl<string[] | null>(null);
  // configsArray solo contiene dias/horaInicio/horaFin — idCliente vive en displayControls
  configsArray = new FormArray<FormGroup<IConfigHorarioForm>>([]);
  displayControls: FormControl<string | null>[] = [];
  showCards = false;
  diasSemana = DIAS;
  disabled = false;

  /** Resumen para la cabecera del desplegable de horarios. */
  get resumenHorarios(): string {
    const personalizados = this.configsArray.controls.some(
      (g) => g.value.dias?.length || g.value.horaInicio || g.value.horaFin,
    );
    return personalizados ? 'Personalizados' : 'Todos los días, todo el día';
  }

  private onChange: (value: IConfigHorario[] | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    // Emitir inmediatamente cuando cambia la selección de clientes
    this.clientesSelectorControl.valueChanges.subscribe(() => {
      if (this.showCards) this.sincronizarTarjetas();
      this.emitChanges();
    });
    this.configsArray.valueChanges.subscribe(() => this.emitChanges());
  }

  writeValue(value: IConfigHorario[] | null): void {
    this.configsArray.clear({ emitEvent: false });
    this.displayControls = [];
    this.showCards = false;

    if (!value?.length) {
      this.clientesSelectorControl.setValue(null, { emitEvent: false });
      return;
    }

    const ids = value.map((c) => c.idCliente).filter(Boolean) as string[];
    this.clientesSelectorControl.setValue(ids.length ? ids : null, {
      emitEvent: false,
    });

    // Solo mostrar tarjetas si hay configuración de horarios guardada
    const hasTimeConfig = value.some(
      (c) => c.dias?.length || c.horaInicio || c.horaFin,
    );
    if (hasTimeConfig) {
      this.showCards = true;
      value.forEach((c) => {
        this.configsArray.push(this.crearGrupo(c), { emitEvent: false });
        this.displayControls.push(
          new FormControl<string | null>({
            value: c.idCliente ?? null,
            disabled: true,
          }),
        );
      });
    }
  }

  registerOnChange(fn: (value: IConfigHorario[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.clientesSelectorControl.disable({ emitEvent: false });
      this.configsArray.disable({ emitEvent: false });
    } else {
      this.clientesSelectorControl.enable({ emitEvent: false });
      this.configsArray.enable({ emitEvent: false });
    }
  }

  private crearGrupo(config?: IConfigHorario): FormGroup<IConfigHorarioForm> {
    return new FormGroup({
      dias: new FormControl<number[] | null>(config?.dias ?? null),
      horaInicio: new FormControl<string | null>(config?.horaInicio ?? null, [
        Validators.pattern(/^([01]\d|2[0-3]):([0-5]\d)$/),
      ]),
      horaFin: new FormControl<string | null>(config?.horaFin ?? null, [
        Validators.pattern(/^([01]\d|2[0-3]):([0-5]\d)$/),
      ]),
    });
  }

  private sincronizarTarjetas(): void {
    const selectedIds = this.clientesSelectorControl.value ?? [];
    const existingIds = this.displayControls.map((c) => c.getRawValue());

    for (const id of selectedIds) {
      if (!existingIds.includes(id)) {
        this.configsArray.push(this.crearGrupo(), { emitEvent: false });
        this.displayControls.push(
          new FormControl<string | null>({ value: id, disabled: true }),
        );
      }
    }

    for (let i = this.configsArray.length - 1; i >= 0; i--) {
      const id = this.displayControls[i]?.getRawValue();
      if (!selectedIds.includes(id ?? '')) {
        this.configsArray.removeAt(i, { emitEvent: false });
        this.displayControls.splice(i, 1);
      }
    }
  }

  generar(): void {
    this.showCards = true;
    this.sincronizarTarjetas();
    this.emitChanges();
    this.onTouched();
  }

  eliminar(index: number): void {
    const id = this.displayControls[index]?.getRawValue();
    this.configsArray.removeAt(index, { emitEvent: false });
    this.displayControls.splice(index, 1);

    if (id) {
      const current = this.clientesSelectorControl.value ?? [];
      const updated = current.filter((s) => s !== id);
      this.clientesSelectorControl.setValue(updated.length ? updated : null, {
        emitEvent: false,
      });
    }

    this.emitChanges();
    this.onTouched();
  }

  private emitChanges(): void {
    const selectedIds = this.clientesSelectorControl.value ?? [];
    if (!selectedIds.length) {
      this.onChange(null);
      return;
    }

    const result: IConfigHorario[] = selectedIds.map((id) => {
      const cardIndex = this.displayControls.findIndex(
        (c) => c.getRawValue() === id,
      );
      if (cardIndex >= 0) {
        const { dias, horaInicio, horaFin } =
          this.configsArray.at(cardIndex).value;
        const entry: IConfigHorario = { idCliente: id };
        if (dias?.length) entry.dias = dias;
        if (horaInicio) entry.horaInicio = horaInicio;
        if (horaFin) entry.horaFin = horaFin;
        return entry;
      }
      return { idCliente: id };
    });

    this.onChange(result);
  }

  onBlur(): void {
    this.onTouched();
  }
}
